import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from 'shared/lib/redux';
import { ALERT_VARIANT } from 'shared/ui/alert';
import {
  isAcceptMyInvitesError,
  useAcceptMyInviteQuery,
} from 'shared/api/manage-onboarding-invites';

import { getAcceptedInviteMessages, ORG_ID_SEARCH_PARAM } from '../constants';
import {
  setAcceptedInviteAlertVariant,
  setAcceptedInviteMessage,
  setIsAcceptedInviteAlertVisible,
} from '../model';

export const useAcceptInvite = () => {
  const { t } = useTranslation();

  const ACCEPTED_INVITE_MESSAGE_MAP = getAcceptedInviteMessages(t);
  const [orgId, setOrgId] = useState('');

  const { data: myAcceptedInviteInfo, error: myAcceptedInviteError } =
    useAcceptMyInviteQuery(
      {
        orgId,
      },
      { skip: !orgId },
    );

  const dispatch = useAppDispatch();

  const getSearchParams = useCallback(() => {
    const currentLocation = new URL(window.location.href);

    return currentLocation.searchParams;
  }, []);

  const removeOrgIdFromUrl = useCallback(() => {
    const searchParams = getSearchParams();
    searchParams.delete(ORG_ID_SEARCH_PARAM);

    const updatedSearchParams = searchParams.toString()
      ? `?${searchParams.toString()}`
      : '';

    window.history.replaceState(
      null,
      '',
      `${window.location.origin}/home/educator${updatedSearchParams}`,
    );
  }, [getSearchParams]);

  useEffect(() => {
    const searchParams = getSearchParams();
    const orgIdSearchParam = searchParams.get(ORG_ID_SEARCH_PARAM);

    if (!orgIdSearchParam) return;

    setOrgId(orgIdSearchParam);
  }, [getSearchParams]);

  useEffect(() => {
    if (!myAcceptedInviteInfo) return;

    const responseCode = myAcceptedInviteInfo.responseCode;

    const acceptedInviteMessage = ACCEPTED_INVITE_MESSAGE_MAP[responseCode];

    if (!acceptedInviteMessage) return;

    dispatch(setIsAcceptedInviteAlertVisible(true));
    dispatch(setAcceptedInviteMessage(acceptedInviteMessage));
    dispatch(setAcceptedInviteAlertVariant(ALERT_VARIANT.SUCCESS));

    removeOrgIdFromUrl();
  }, [myAcceptedInviteInfo, dispatch, removeOrgIdFromUrl]);

  useEffect(() => {
    if (
      !myAcceptedInviteError ||
      !isAcceptMyInvitesError(myAcceptedInviteError)
    )
      return;

    const responseCode = myAcceptedInviteError.responseCode;

    const acceptedInviteMessage = ACCEPTED_INVITE_MESSAGE_MAP[responseCode];

    if (!acceptedInviteMessage) return;

    dispatch(setIsAcceptedInviteAlertVisible(true));
    dispatch(setAcceptedInviteMessage(acceptedInviteMessage));
    dispatch(setAcceptedInviteAlertVariant(ALERT_VARIANT.ERROR));

    removeOrgIdFromUrl();
  }, [myAcceptedInviteError, dispatch, removeOrgIdFromUrl]);
};
